import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Star, MapPin, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TeamLogo } from "@/components/team/teamShared";
import EditClubDialog from "@/components/team/EditClubDialog";
import TeamOverview from "@/components/team/TeamOverview";
import TeamAthletes from "@/components/team/TeamAthletes";
import TeamRelations, { NextActionDialog } from "@/components/team/TeamRelations";
import TeamOpportunities from "@/components/team/TeamOpportunities";
import TeamActivities from "@/components/team/TeamActivities";
import TeamDocuments from "@/components/team/TeamDocuments";
import { translateFr as tfr } from "@/i18n";

const TABS = [
  { id: "overview", label: tfr("Vue d'ensemble") },
  { id: "athletes", label: tfr("Athlètes") },
  { id: "relations", label: tfr("Relations") },
  { id: "opportunities", label: tfr("Opportunités") },
  { id: "activities", label: tfr("Activités") },
  { id: "documents", label: "Documents" },
];

export default function TeamProfile() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "overview";
  const [ov, setOv] = useState(null);
  const [error, setError] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/clubs/${cid}/overview`).then((r) => { setOv(r.data); setError(false); }).catch(() => setError(true));
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });
  const club = ov?.club;

  const toggleFav = async () => {
    try {
      const r = await api.patch(`/clubs/${cid}/relationship`, { favorite: !club?.favorite });
      setOv((o) => ({ ...o, club: r.data }));
    } catch { toast.error(tfr("Action impossible")); }
  };

  if (error) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="team-error">
        {tfr("Impossible de charger cette équipe.")}
        <div className="mt-3 flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={load}>{tfr("Réessayer")}</Button>
          <Button variant="ghost" size="sm" onClick={() => navigate("/clubs")}>{tfr("Retour aux équipes")}</Button>
        </div>
      </div>
    );
  }
  if (!ov) {
    return (
      <div className="p-4 md:p-6 space-y-4" data-testid="team-skeleton">
        <div className="h-20 rounded-2xl bg-muted/60 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl bg-muted/60 animate-pulse" />)}</div>
        <div className="h-64 rounded-2xl bg-muted/60 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="team-profile">
      {/* Header */}
      <div className="flex items-start gap-3 md:gap-4">
        <button onClick={() => navigate("/clubs")} aria-label={tfr("Retour aux équipes")} data-testid="team-back"
          className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0 mt-1">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <TeamLogo club={club} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight truncate" data-testid="team-name">{club.name}</h1>
            <button onClick={toggleFav} aria-label={club.favorite ? tfr("Retirer des favoris") : tfr("Ajouter aux favoris")} data-testid="team-favorite"
              className="text-muted-foreground hover:text-amber-500 transition-colors">
              <Star className={cn("h-4 w-4", club.favorite && "fill-amber-400 text-amber-400")} />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {[club.sport, club.competition || club.league].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden />{[club.city, club.country].filter(Boolean).join(", ") || "—"}
          </p>
          {club.description && <p className="text-xs text-muted-foreground/90 mt-1.5 max-w-xl" data-testid="team-description">{club.description}</p>}
          {(club.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {club.tags.slice(0, 5).map((t) => (
                <span key={t} className="text-[10px] rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-muted-foreground">{t}</span>
              ))}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="h-9 gap-1.5 shrink-0" data-testid="team-edit">
          <Pencil className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{tfr("Modifier")}</span>
        </Button>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label={tfr("Sections équipe")}
        className="flex gap-1 overflow-x-auto border-b border-border mt-5 -mx-4 px-4 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`team-tab-${t.id}`}
            className={cn("px-3 h-11 text-sm whitespace-nowrap shrink-0 border-b-2 -mb-px transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tab === t.id ? "border-athlynx text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "overview" && <TeamOverview ov={ov} onGoTab={setTab} onChanged={load} onPlanAction={() => setActionOpen(true)} />}
        {tab === "athletes" && <TeamAthletes cid={cid} onChanged={load} />}
        {tab === "relations" && <TeamRelations cid={cid} club={club} health={ov.health} onChanged={load} />}
        {tab === "opportunities" && <TeamOpportunities cid={cid} onChanged={load} />}
        {tab === "activities" && <TeamActivities cid={cid} />}
        {tab === "documents" && <TeamDocuments cid={cid} onChanged={load} />}
      </div>

      <NextActionDialog cid={cid} open={actionOpen} onOpenChange={setActionOpen} onCreated={load} />
      <EditClubDialog club={club} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />
    </div>
  );
}
